import React, { useState, useRef } from 'react';
import {
    View, Text, StyleSheet, TouchableOpacity, Modal,
    Image, ScrollView, Dimensions, Alert, StatusBar, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import PhotoClipPlayer from './PhotoClipPlayer';

const { width: SW, height: SH } = Dimensions.get('window');
const THUMB = 80;

const STYLES = [
    { key: 'kenBurns',  label: 'Ken Burns', icon: 'film',           desc: 'Cinematic zoom & pan',  accent: '#7c3aed' },
    { key: 'crossfade', label: 'Crossfade', icon: 'layers',          desc: 'Smooth fade transition', accent: '#0891b2' },
    { key: 'slide',     label: 'Slide',     icon: 'arrow-forward',   desc: 'Horizontal slide',       accent: '#059669' },
    { key: 'zoomBurst', label: 'Zoom',      icon: 'expand',          desc: 'Energetic zoom burst',   accent: '#d97706' },
];

const DURATIONS = [
    { label: '1.5s', value: 1500 },
    { label: '2s',   value: 2000 },
    { label: '3s',   value: 3000 },
];

const PhotoClipPicker = ({ visible, onClose, onClipSelected }) => {
    const insets = useSafeAreaInsets();

    const [step, setStep] = useState('select'); // 'select' | 'style' | 'preview'
    const [images, setImages] = useState([]);
    const [animStyle, setAnimStyle] = useState('kenBurns');
    const [duration, setDuration] = useState(2000);
    const [picking, setPicking] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadDone, setUploadDone] = useState(false);
    const uploadTimer = useRef(null);

    const reset = () => {
        setStep('select'); setImages([]); setAnimStyle('kenBurns'); setDuration(2000);
        setUploading(false); setUploadProgress(0); setUploadDone(false);
        if (uploadTimer.current) clearInterval(uploadTimer.current);
    };

    const handleClose = () => { reset(); onClose(); };

    // ── Step 1: pick images ──────────────────────────────────────────────────
    const pickImages = async () => {
        if (picking) return;
        const remaining = 10 - images.length;
        if (remaining <= 0) { Alert.alert('Max 10 images'); return; }
        try {
            const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (status !== 'granted') { Alert.alert('Permission Required', 'Please allow media library access.'); return; }
            setPicking(true);
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsMultipleSelection: true,
                selectionLimit: remaining,
                quality: 0.85,
            });
            if (!result.canceled && result.assets?.length) {
                const incoming = result.assets.map(a => ({ uri: a.uri, width: a.width, height: a.height }));
                setImages(prev => [...prev, ...incoming].slice(0, 10));
            }
        } catch (e) {
            Alert.alert('Error', 'Could not open gallery.');
        } finally {
            setPicking(false);
        }
    };

    const removeImage = (index) => setImages(prev => prev.filter((_, i) => i !== index));

    const moveLeft = (index) => {
        if (index === 0) return;
        setImages(prev => { const a = [...prev]; [a[index - 1], a[index]] = [a[index], a[index - 1]]; return a; });
    };
    const moveRight = (index) => {
        setImages(prev => {
            if (index >= prev.length - 1) return prev;
            const a = [...prev]; [a[index], a[index + 1]] = [a[index + 1], a[index]]; return a;
        });
    };

    // ── Step 3: use clip ─────────────────────────────────────────────────────
    const useClip = () => {
        setUploading(true);
        setUploadProgress(0);
        uploadTimer.current = setInterval(() => {
            setUploadProgress(prev => {
                if (prev >= 1) { clearInterval(uploadTimer.current); setUploading(false); setUploadDone(true); return 1; }
                return prev + 0.05;
            });
        }, 100);
    };

    const finalize = () => {
        onClipSelected({
            images: images.map(i => i.uri),
            animationConfig: { style: animStyle, durationPerSlide: duration, transitionDuration: Math.round(duration * 0.3) },
        });
        reset(); onClose();
    };

    const config = { style: animStyle, durationPerSlide: duration, transitionDuration: Math.round(duration * 0.3) };
    const previewH = Math.round(SW * 1.2);

    if (!visible) return null;

    return (
        <Modal visible={visible} animationType="slide" transparent={false} statusBarTranslucent onRequestClose={handleClose}>
            <View style={s.root}>
                <StatusBar barStyle="light-content" backgroundColor="#000" />
                <LinearGradient colors={['#07000f', '#0d0020', '#000']} style={StyleSheet.absoluteFill} />

                {/* ── STEP 1: Select images ── */}
                {step === 'select' && (
                    <View style={[s.page, { paddingTop: insets.top + 16, paddingBottom: insets.bottom + 24 }]}>
                        <View style={s.hdr}>
                            <TouchableOpacity style={s.closeBtn} onPress={handleClose}>
                                <Ionicons name="close" size={20} color="#fff" />
                            </TouchableOpacity>
                            <Text style={s.hdrTitle}>Photo Slideshow</Text>
                            <View style={{ width: 40 }} />
                        </View>

                        {/* Info pill */}
                        <View style={s.infoPill}>
                            <Ionicons name="images-outline" size={13} color="#a78bfa" />
                            <Text style={s.infoPillText}>
                                Select <Text style={{ color: '#c4b5fd', fontWeight: '900' }}>2–10 photos</Text> to animate
                            </Text>
                        </View>

                        {/* Selected thumbnails */}
                        {images.length > 0 && (
                            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.thumbScroll} contentContainerStyle={s.thumbRow}>
                                {images.map((img, i) => (
                                    <View key={i} style={s.thumbWrap}>
                                        <Image source={{ uri: img.uri }} style={s.thumb} resizeMode="cover" />
                                        <View style={s.thumbOrder}><Text style={s.thumbOrderText}>{i + 1}</Text></View>
                                        <TouchableOpacity style={s.thumbRemove} onPress={() => removeImage(i)}>
                                            <Ionicons name="close-circle" size={18} color="#fff" />
                                        </TouchableOpacity>
                                        {/* Reorder arrows */}
                                        <View style={s.thumbArrows}>
                                            {i > 0 && (
                                                <TouchableOpacity onPress={() => moveLeft(i)} style={s.arrowBtn}>
                                                    <Ionicons name="chevron-back" size={12} color="#fff" />
                                                </TouchableOpacity>
                                            )}
                                            {i < images.length - 1 && (
                                                <TouchableOpacity onPress={() => moveRight(i)} style={s.arrowBtn}>
                                                    <Ionicons name="chevron-forward" size={12} color="#fff" />
                                                </TouchableOpacity>
                                            )}
                                        </View>
                                    </View>
                                ))}
                                {images.length < 10 && (
                                    <TouchableOpacity style={s.addMore} onPress={pickImages} disabled={picking}>
                                        <Ionicons name="add" size={28} color="rgba(255,255,255,0.4)" />
                                    </TouchableOpacity>
                                )}
                            </ScrollView>
                        )}

                        {/* Pick button */}
                        <TouchableOpacity style={s.pickCard} onPress={pickImages} disabled={picking} activeOpacity={0.85}>
                            <LinearGradient colors={['rgba(124,58,237,0.25)', 'rgba(91,33,182,0.1)', 'transparent']} style={s.pickGrad}>
                                <View style={s.pickOrb}>
                                    {picking
                                        ? <ActivityIndicator color="#a78bfa" />
                                        : <Ionicons name="images" size={38} color="#a78bfa" />}
                                </View>
                                <Text style={s.pickTitle}>{images.length === 0 ? 'Choose Photos' : 'Add More Photos'}</Text>
                                <Text style={s.pickSub}>Select from your gallery · {images.length}/10 selected</Text>
                                <LinearGradient colors={['#7c3aed', '#5b21b6']} style={s.pickBtn} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                                    <Ionicons name="folder-open-outline" size={16} color="#fff" />
                                    <Text style={s.pickBtnText}>{picking ? 'Opening…' : 'Browse Gallery'}</Text>
                                </LinearGradient>
                            </LinearGradient>
                        </TouchableOpacity>

                        {/* Next */}
                        {images.length >= 2 && (
                            <TouchableOpacity style={s.nextBtn} onPress={() => setStep('style')} activeOpacity={0.85}>
                                <LinearGradient colors={['#7c3aed', '#5b21b6']} style={s.nextGrad} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                                    <Text style={s.nextText}>Choose Animation →</Text>
                                </LinearGradient>
                            </TouchableOpacity>
                        )}
                    </View>
                )}

                {/* ── STEP 2: Choose style & duration ── */}
                {step === 'style' && (
                    <View style={[s.page, { paddingTop: insets.top + 16, paddingBottom: insets.bottom + 24 }]}>
                        <View style={s.hdr}>
                            <TouchableOpacity style={s.closeBtn} onPress={() => setStep('select')}>
                                <Ionicons name="chevron-back" size={20} color="#fff" />
                            </TouchableOpacity>
                            <Text style={s.hdrTitle}>Animation Style</Text>
                            <View style={{ width: 40 }} />
                        </View>

                        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ gap: 12, paddingBottom: 20 }}>
                            {/* Style cards */}
                            {STYLES.map(st => (
                                <TouchableOpacity
                                    key={st.key}
                                    style={[s.styleCard, animStyle === st.key && { borderColor: st.accent, borderWidth: 1.5 }]}
                                    onPress={() => setAnimStyle(st.key)}
                                    activeOpacity={0.85}
                                >
                                    <View style={[s.styleIconOrb, { backgroundColor: st.accent + '22' }]}>
                                        <Ionicons name={st.icon} size={22} color={st.accent} />
                                    </View>
                                    <View style={s.styleInfo}>
                                        <Text style={s.styleLabel}>{st.label}</Text>
                                        <Text style={s.styleDesc}>{st.desc}</Text>
                                    </View>
                                    {animStyle === st.key && (
                                        <Ionicons name="checkmark-circle" size={22} color={st.accent} />
                                    )}
                                </TouchableOpacity>
                            ))}

                            {/* Duration */}
                            <Text style={s.sectionLabel}>TIME PER PHOTO</Text>
                            <View style={s.durationRow}>
                                {DURATIONS.map(d => (
                                    <TouchableOpacity
                                        key={d.value}
                                        style={[s.durChip, duration === d.value && s.durChipActive]}
                                        onPress={() => setDuration(d.value)}
                                    >
                                        <Text style={[s.durChipText, duration === d.value && s.durChipTextActive]}>
                                            {d.label}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </ScrollView>

                        <TouchableOpacity style={s.nextBtn} onPress={() => setStep('preview')} activeOpacity={0.85}>
                            <LinearGradient colors={['#7c3aed', '#5b21b6']} style={s.nextGrad} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                                <Text style={s.nextText}>Preview Clip →</Text>
                            </LinearGradient>
                        </TouchableOpacity>
                    </View>
                )}

                {/* ── STEP 3: Preview ── */}
                {step === 'preview' && (
                    <View style={s.previewRoot}>
                        {/* Full-screen player */}
                        <PhotoClipPlayer
                            images={images.map(i => i.uri)}
                            animationConfig={config}
                            width={SW}
                            height={SH}
                            borderRadius={0}
                            autoPlay
                        />

                        {/* Overlay: back */}
                        <TouchableOpacity style={[s.backBtn, { top: insets.top + 12 }]} onPress={() => setStep('style')}>
                            <Ionicons name="chevron-back" size={22} color="#fff" />
                        </TouchableOpacity>

                        {/* Bottom panel */}
                        <LinearGradient
                            colors={['transparent', 'rgba(0,0,0,0.92)']}
                            style={[s.previewPanel, { paddingBottom: insets.bottom + 24 }]}
                            pointerEvents="box-none"
                        >
                            <View style={s.metaRow}>
                                <View style={s.metaBadge}>
                                    <Ionicons name="images-outline" size={12} color="#a78bfa" />
                                    <Text style={s.metaText}>{images.length} photos  ·  {STYLES.find(x => x.key === animStyle)?.label}</Text>
                                </View>
                                <View style={s.metaBadge}>
                                    <Ionicons name="flash" size={11} color="#a78bfa" />
                                    <Text style={s.metaText}>Lightweight · No video</Text>
                                </View>
                            </View>

                            {/* CTA */}
                            {uploadDone ? (
                                <TouchableOpacity style={s.cta} onPress={finalize} activeOpacity={0.85}>
                                    <LinearGradient colors={['#10b981', '#059669']} style={s.ctaIn} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                                        <Ionicons name="arrow-forward-circle" size={20} color="#fff" />
                                        <Text style={s.ctaText}>Continue to Post</Text>
                                    </LinearGradient>
                                </TouchableOpacity>
                            ) : (
                                <TouchableOpacity
                                    style={[s.cta, uploading && { opacity: 0.75 }]}
                                    onPress={useClip}
                                    activeOpacity={0.85}
                                    disabled={uploading}
                                >
                                    <LinearGradient colors={['#7c3aed', '#5b21b6']} style={s.ctaIn} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                                        {uploading
                                            ? <ActivityIndicator color="#fff" style={{ marginRight: 8 }} />
                                            : <Ionicons name="checkmark-circle" size={20} color="#fff" />}
                                        <Text style={s.ctaText}>
                                            {uploading ? `Processing ${Math.round(uploadProgress * 100)}%` : 'Use this Slideshow'}
                                        </Text>
                                    </LinearGradient>
                                </TouchableOpacity>
                            )}
                        </LinearGradient>
                    </View>
                )}
            </View>
        </Modal>
    );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
    root: { flex: 1, backgroundColor: '#000' },
    page: { flex: 1, paddingHorizontal: 20 },

    hdr: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
    hdrTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
    closeBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.08)', alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },

    infoPill: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: 'rgba(124,58,237,0.12)', borderWidth: 1, borderColor: 'rgba(124,58,237,0.25)', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, alignSelf: 'center', marginBottom: 24 },
    infoPillText: { color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: '600' },

    // Thumbnails
    thumbScroll: { flexGrow: 0, marginBottom: 16 },
    thumbRow: { gap: 10, paddingVertical: 4 },
    thumbWrap: { width: THUMB, height: THUMB, borderRadius: 10, overflow: 'hidden', position: 'relative' },
    thumb: { width: THUMB, height: THUMB },
    thumbOrder: { position: 'absolute', top: 4, left: 4, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 8, paddingHorizontal: 5, paddingVertical: 1 },
    thumbOrderText: { color: '#fff', fontSize: 9, fontWeight: '900' },
    thumbRemove: { position: 'absolute', top: 3, right: 3 },
    thumbArrows: { position: 'absolute', bottom: 3, left: 0, right: 0, flexDirection: 'row', justifyContent: 'center', gap: 4 },
    arrowBtn: { backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 6, padding: 3 },
    addMore: { width: THUMB, height: THUMB, borderRadius: 10, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.15)', borderStyle: 'dashed', alignItems: 'center', justifyContent: 'center' },

    // Pick card
    pickCard: { flex: 1, borderRadius: 20, overflow: 'hidden', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', marginBottom: 16 },
    pickGrad: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24 },
    pickOrb: { width: 80, height: 80, borderRadius: 40, backgroundColor: 'rgba(139,92,246,0.18)', alignItems: 'center', justifyContent: 'center' },
    pickTitle: { color: '#fff', fontSize: 18, fontWeight: '800' },
    pickSub: { color: 'rgba(255,255,255,0.5)', fontSize: 13, textAlign: 'center' },
    pickBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: 20, paddingHorizontal: 22, paddingVertical: 11 },
    pickBtnText: { color: '#fff', fontSize: 14, fontWeight: '800' },

    // Style cards
    styleCard: { flexDirection: 'row', alignItems: 'center', gap: 14, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: 14, borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)' },
    styleIconOrb: { width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
    styleInfo: { flex: 1 },
    styleLabel: { color: '#fff', fontSize: 15, fontWeight: '800', marginBottom: 2 },
    styleDesc: { color: 'rgba(255,255,255,0.45)', fontSize: 12 },

    sectionLabel: { color: 'rgba(255,255,255,0.3)', fontSize: 10, fontWeight: '800', letterSpacing: 1, marginTop: 8 },
    durationRow: { flexDirection: 'row', gap: 10 },
    durChip: { flex: 1, borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)', paddingVertical: 10, alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.04)' },
    durChipActive: { borderColor: '#7c3aed', backgroundColor: 'rgba(124,58,237,0.18)' },
    durChipText: { color: 'rgba(255,255,255,0.5)', fontWeight: '700', fontSize: 14 },
    durChipTextActive: { color: '#c4b5fd' },

    // Next button
    nextBtn: { borderRadius: 28, overflow: 'hidden', marginTop: 8 },
    nextGrad: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16 },
    nextText: { color: '#fff', fontSize: 15, fontWeight: '900' },

    // Preview
    previewRoot: { flex: 1, position: 'relative' },
    backBtn: { position: 'absolute', left: 14, width: 38, height: 38, borderRadius: 19, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)' },
    previewPanel: { position: 'absolute', bottom: 0, left: 0, right: 0, paddingHorizontal: 20, paddingTop: 60, gap: 14 },
    metaRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    metaBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 5 },
    metaText: { color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: '600' },

    cta: { borderRadius: 28, overflow: 'hidden' },
    ctaIn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, paddingVertical: 16 },
    ctaText: { color: '#fff', fontSize: 15, fontWeight: '900' },
});

export default PhotoClipPicker;
